import { Navigate } from 'react-router-dom';

import useAuth from '../hooks/useAuth';

import { PATH_PAGE, PATH_AUTH } from '../routes/paths';
import { useSelector } from '../redux/store';
import { isOnTimeUserLogin } from '../utils/sessionFlags';

const GuestGuard = () => {
  const { env, isAuthenticated, isDevicePaired, isResource, socketUrl, user } = useAuth();
  const { clockingDeviceSites } = useSelector((state) => state.clockings);

  if (!isAuthenticated || !Object.keys(user).length) {
    // Application mobile, connexion au webservice d'une société
    if (window.env.isApp && !socketUrl) {
      return <Navigate to={PATH_AUTH.chooseCompany} />;
    }

    // Application Comète On Time
    if (window.env.isClockingApp) {
      // Page d'information : Comète On Time n'est pas activée
      if (!env.isClockingSiteEnabled) {
        return <Navigate to={PATH_AUTH.clockingApp.clockingAppDisabled} />;
      }
      // Page de connexion d'un manager ou initialisation de la pointeuse
      return (
        <Navigate
          to={
            isDevicePaired || clockingDeviceSites[0] ? PATH_AUTH.clockingApp.login : PATH_AUTH.clockingApp.clockingInit
          }
        />
      );
    }

    return <Navigate to={PATH_AUTH.login} />;
  }

  // Application Comète On Time
  if (window.env.isClockingApp || user?.role === 'clocking_device') {
    // Page d'information : Comète On Time n'est pas activée
    if (!env.isClockingSiteEnabled) {
      return <Navigate to={PATH_AUTH.clockingApp.clockingAppDisabled} />;
    }
    const isEmailLogin = isOnTimeUserLogin();
    // Utilisateur OnTime déconnecté (flag email/code retiré) mais user encore en state :
    // resource, client OU manager → tous ramenés sur /pointages (ClockingInAndOutPage =
    // code + email), peu importe l'état du pairing. Évite un flash vers /pointages/pairing
    // ou /pointages/login après logout (voir AuthGuard pour la même règle).
    // Exception : manager + device non appairé → setup (device-type / pairing) car c'est
    // lui qui configure la pointeuse/MCE après login.
    if (!isEmailLogin && (user?.role === 'resource' || user?.role === 'client' || user?.role === 'manager')) {
      // Flag posé par `OnTimeMenuItems.handleLogout` : si présent, on revient
      // d'un logout intentionnel → on force le retour sur /pointages et on
      // n'évalue PAS la branche device-setup (qui pourrait bouncer vers
      // /pointages/pairing à cause d'un `isDevicePaired` transitoirement false).
      const justLoggedOut = sessionStorage.getItem('ontime_just_logged_out');
      if (justLoggedOut) {
        sessionStorage.removeItem('ontime_just_logged_out');
        return <Navigate to={PATH_AUTH.clockingApp.clockingInAndOut} />;
      }
      if (user?.role === 'manager' && !isDevicePaired) {
        const hasDeviceType = !!localStorage.getItem('comete_device_type');
        return (
          <Navigate to={hasDeviceType ? PATH_AUTH.clockingApp.devicePairing : PATH_AUTH.clockingApp.clockingDeviceType} />
        );
      }
      return <Navigate to={PATH_AUTH.clockingApp.clockingInAndOut} />;
    }
    // Client : pas besoin de pairing, accès direct au dashboard
    if (user?.role === 'client') {
      return <Navigate to={PATH_AUTH.clockingApp.clientHome} />;
    }
    // Manager connecté par email : accès direct au dashboard
    if (user?.role === 'manager' && sessionStorage.getItem('ontime_email_login')) {
      return <Navigate to={PATH_AUTH.clockingApp.managerHome} />;
    }
    // Agent connecté (email ou code MCE) : accès direct au dashboard, pas de pairing.
    // Le pairing concerne `clocking_device` (la tablette), pas l'agent — son `isDevicePaired`
    // peut être `false` en dev/CI sans bloquer son accès.
    if (isResource && isEmailLogin) {
      return <Navigate to={PATH_AUTH.clockingApp.resourceHome} />;
    }
    // Page de pointage ou initialisation de la pointeuse
    if (!isDevicePaired) {
      return <Navigate to={PATH_AUTH.clockingApp.devicePairing} />;
    }
    if (isResource) {
      return <Navigate to={PATH_AUTH.clockingApp.resourceHome} />;
    }
    return <Navigate to={PATH_AUTH.clockingApp.clockingInAndOut} />;
  }

  if (isResource && !user?.hasOneContractActive) {
    return <Navigate to={PATH_PAGE[user.role].missions} />;
  }

  return <Navigate to={PATH_PAGE[user.role].root} />;
};

export default GuestGuard;
